/**
 * A rectangle of ground, as [west, south, east, north].
 *
 * The order MapLibre and GeoJSON both use, and the order the catalogue's
 * bbox parameter expects, so it travels from the map to the query without
 * anybody reordering it on the way.
 */
export type ViewBox = [number, number, number, number];

/**
 * The ground worth fetching for a view of this much.
 *
 * Grown by a third on each side, so a small pan stays inside what has
 * already been read and costs nothing. Larger would fetch a city's worth to
 * show a neighbourhood; smaller and every nudge of the map is a request.
 */
export function wider([west, south, east, north]: ViewBox): ViewBox {
  const padX = (east - west) / 3;
  const padY = (north - south) / 3;

  return [west - padX, south - padY, east + padX, north + padY];
}

/** Whether everything in view has already been fetched. */
export function covers(outer: ViewBox, inner: ViewBox): boolean {
  return outer[0] <= inner[0]
    && outer[1] <= inner[1]
    && outer[2] >= inner[2]
    && outer[3] >= inner[3];
}

/** Whether a point falls inside this rectangle. */
export function holds(box: ViewBox, point: { lat: number; lng: number }): boolean {
  return point.lng >= box[0]
    && point.lat >= box[1]
    && point.lng <= box[2]
    && point.lat <= box[3];
}
